import { FilterOperators } from "@crispico/foundation-gwt-js";
import { createSliceFoundation, EditMode, EntityEditorFormSimple, EntityEditorPage, getBaseImpures, getBaseReducers, PropsFrom, SliceEntityEditorPage, sliceEntityEditorPageOnlyForExtension, Utils } from "@crispico/foundation-react";
import { Scriptable } from "@famiprog-foundation/scriptable-ui";
import { FoundationUtils } from "@famiprog-foundation/utils";
import gql from "graphql-tag";
import React from "react";
import { Header, Menu } from "semantic-ui-react";
import { apolloClientHolder } from "../apolloClient";
import { Filter } from "../components/CustomQuery/Filter";
import { Wizard, WizardRRC, WizardStep } from "../components/Wizard/Wizard";
import { EntityTableSimpleRRC } from "../entity_crud/EntityTableSimple";
import { FindByFilterParams } from "../entity_crud/FindByFilterParams";
import { backgroundTaskEntityDescriptor } from "../FoundationEntityDescriptors";
import { BackgroundTask, BackgroundTaskStatus } from "./BackgroundTaskEntityDescriptor";
import { ReportDescriptorMaterializer } from "./ReportDescriptorMaterializer";
import { ReportDescriptorMaterializers } from "./ReportDescriptorMaterializers";

export const backgroundTaskEditorPageTestids = FoundationUtils.createTestids("BackgroundTaskEditorPage", {
    report: "", recentReportsTable: ""
});

export const sliceBackgroundTaskEditorPage = createSliceFoundation(class Ext extends SliceEntityEditorPage {
    initialState = {
        ...sliceEntityEditorPageOnlyForExtension.initialState,
        recentBackgroundTasks: [] as BackgroundTask[],
    }

    nestedSlices = {
        ...sliceEntityEditorPageOnlyForExtension.nestedSlices,
    }

    reducers = {
        ...sliceEntityEditorPageOnlyForExtension.reducers, ...getBaseReducers<Ext>(this),
    }

    impures = {
        ...sliceEntityEditorPageOnlyForExtension.impures, ...getBaseImpures<Ext>(this),
    }

});

export class BackgroundTaskEditorPage extends EntityEditorPage<PropsFrom<typeof sliceBackgroundTaskEditorPage>> {
    
    protected paramFormRef = React.createRef<EntityEditorFormSimple>();
    protected wizardRef = React.createRef<Wizard>();

    protected getEntity(): BackgroundTask {
        return this.props.entity as BackgroundTask;
    }

    protected async loadRecentBackgroundTasks() {
        if (!this.getEntity().reportIdAndSubId) return;

        const query = `query backgroundTaskService_findByFilter($params: FindByFilterParamsInput) {
                backgroundTaskService_findByFilter(params: $params) { 
                    results { id, reportIdAndSubId, finishedAt, generatedFiles, paramJson } 
                }
            }`;
        const tasks = (await apolloClientHolder.apolloClient.query({
            query: gql(query),
            variables: FindByFilterParams.create()
                .filter(Filter.createComposed(FilterOperators.forComposedFilter.and, [
                    Filter.create("reportIdAndSubId", FilterOperators.forString.equals, this.getEntity().reportIdAndSubId),
                    Filter.create("status", FilterOperators.forString.equals, BackgroundTaskStatus.FINISHED)]))
                .sorts([{ field: "createdAt", direction: "DESC" }])
                .pageSize(10)
        })).data.backgroundTaskService_findByFilter.results as BackgroundTask[];
        this.props.dispatchers.setInReduxState({ recentBackgroundTasks: tasks });
    }

    protected commitMain() {
        super.commitMain();
        if (this.props.mode === EditMode.ADD) {
            this.props.dispatchers.setInReduxState({
                entity: {
                    ...this.props.entity,
                    user: this.context.initializationsForClient.currentUser,
                    status: BackgroundTaskStatus.PENDING,
                    createdAt: Utils.now().toISOString(),
                    paramJson: JSON.stringify(this.paramFormRef.current!.formikContext.values)
                }
            });
        }
    }

    protected getContainerCssClasses() {
        const css = super.getContainerCssClasses();
        if (this.props.mode === EditMode.ADD) {
            return css;
        }
        // for EDIT MODE, disable /edit page entirely
        return {...css, outer: `BackgroundTaskEditorPage_editMode ${css.outer}`};
    }

    protected renderForm() {
        if (this.props.mode !== EditMode.ADD) {
            return super.renderForm();
        }
        const steps: WizardStep[] = [{
                title: _msg("BackgroundTaskEditorPage.wizard.step1"),
                icon: "question circle outline",
                nextDisabled: () => Utils.isNullOrEmpty(this.getEntity().reportIdAndSubId),
                onNextClick: async () => {
                    await this.loadRecentBackgroundTasks();
                    if (!this.props.recentBackgroundTasks || this.props.recentBackgroundTasks.length === 0) {
                        if (this.wizardRef.current) {
                            this.wizardRef.current.props.r.setInReduxState({ currentStepIndex: 2 });
                        }
                        return false;
                    }
                    return true;
                },
                render: () => (
                    <Menu fluid vertical>
                        {Object.keys(ReportDescriptorMaterializers.INSTANCE.getRegistry()).map(reportId => {
                            const report: ReportDescriptorMaterializer = ReportDescriptorMaterializers.INSTANCE.getRegistry()[reportId];
                            return <Scriptable key={"report_" + reportId} id={`Report_${reportId}`} onClick={() => this.props.dispatchers.setInReduxState({
                                entity: {
                                    ...this.props.entity,
                                    reportIdAndSubId: reportId,
                                    bean: report.reportDescriptor.bean,
                                    method: report.reportDescriptor.method
                                }
                            })}>{scriptable =>
                                <Menu.Item data-testid={backgroundTaskEditorPageTestids.report + "_" + reportId} className="flex-container-row"
                                    active={reportId === this.getEntity().reportIdAndSubId} link onClick={() => scriptable.proxy.onClick()}>
                                    <div className="flex-grow">
                                        <Header as="h3" dividing>{report.getLabel()}</Header>
                                        <p>{report.getDescription()}</p>
                                    </div>
                                </Menu.Item>
                                }</Scriptable>
                        })}
                    </Menu>
                ),
            }, {
                title: _msg("BackgroundTaskEditorPage.wizard.step2"),
                icon: "list",
                render: () => {
                    return this.props.recentBackgroundTasks.length === 0
                        ? <div>{_msg("general.no.data")}</div>
                        : <EntityTableSimpleRRC data-testid={backgroundTaskEditorPageTestids.recentReportsTable}
                            id="recentBackgroundTasksTable" tableProps={{ height: 250 }}
                            entityDescriptor={backgroundTaskEntityDescriptor} entitiesAsParams={this.props.recentBackgroundTasks}
                            columns={[
                                { name: "finishedAt", width: 150 },
                                { name: "generatedFiles", width: 400 },
                                { name: "paramJson", width: 50 }
                            ]}
                        />;
                }
            }, {
                title: _msg("BackgroundTaskEditorPage.wizard.step3"),
                icon: "edit",
                render: () => {
                    const report: ReportDescriptorMaterializer = ReportDescriptorMaterializers.INSTANCE.getRegistry()[this.getEntity().reportIdAndSubId!];
                    return report.renderParamEditorForm(this.paramFormRef, this.getEntity().paramJson);
                }
            }
        ];
        return <WizardRRC ref={this.wizardRef} id="wizardChart" steps={steps} onFinishClick={() => this.onSave()} onCancelClick={this.onCancel} />
    }

}"./.."